
    import __i18nConfig from '@next-translate-root/i18n'
    import __appWithI18n from 'next-translate/appWithI18n'
    import useTranslation from "next-translate/useTranslation";
import Link from "next/link";
import { useEffect } from "react";
import { connect, ConnectedProps, useDispatch } from "react-redux";
import { Card, CardBody, CardHeader, Col, Row, Spinner } from "reactstrap";
import { AnyAction, Dispatch } from "redux";
import { FeedbackInvitationState, ICategory, IFeedbackInvitation, IProcess, UserRole } from "@api/schema";
import BaseLayout from "@components/BaseLayout";
import AuthElement from "@components/common/AuthElement";
import PageError from "@components/common/PageError";
import TranslatedHtml from "@components/common/TranslatedHtml";
import ErrorPage from "@components/ErrorPage";
import FeedbackInvitationQuestionCard from "@components/feedback/FeedbackInvitationQuestionCard";
import ProcessView from "@components/process/ProcessView";
import { loadCurrentProcessAction, ProcessActionTypes } from "@redux/actions/processes";
import { newLoadCollectionAction } from "@redux/helper/actions";
import { AppState } from "@redux/reducer";
import { selectCurrentUser, selectRoles } from "@redux/reducer/auth";
import { selectCollectionUsecaseState, selectCurrentProcess, selectSingleEntityUsecaseState } from "@redux/reducer/data";
import { IFeedbackInvitationFilterCriteria } from "@redux/reducer/feedback";
import { EntityType, ScopeTypes } from "@redux/reduxTypes";
import { loadCurrentUserAction } from "@redux/usecases/userAccount";
import { isAdmin, isProcessManager } from "@services/authHelper";
import { Routes } from "@services/routes";
import { MODULE_FEEDBACK_FOR_PM_AVAILABLE } from "config";
const mapStateToProps = (state: AppState) => ({
    categoryRequest: selectCollectionUsecaseState(state, EntityType.Category, ScopeTypes.CategoriesLoading),
    currentProcess: selectCurrentProcess(state),
    currentUser: selectCurrentUser(state),
    /** state of a request for FBIs */
    fbiStateForPublicProcess: selectCollectionUsecaseState(state, EntityType.FeedbackInvitation, ScopeTypes.FBIForPublic),
    /** state of a request for the process */
    processRequest: selectSingleEntityUsecaseState(state, EntityType.Process, ProcessActionTypes.LoadCurrentProcess),
    roles: selectRoles(state),
});
const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => ({
    loadCurrentProcess: () => dispatch(loadCurrentProcessAction()),
    loadCurrentUser: () => dispatch(loadCurrentUserAction()),
    loadFBIs: (relatedObject: IProcess, receiver?: string | string[], state?: FeedbackInvitationState, anonymousFeedback?: boolean) => dispatch(newLoadCollectionAction(EntityType.FeedbackInvitation, { relatedObject: relatedObject["@id"], receiver, state, anonymousFeedback } as IFeedbackInvitationFilterCriteria, ScopeTypes.FBIForPublic)),
});
const connector = connect(mapStateToProps, mapDispatchToProps);
type PageProps = ConnectedProps<typeof connector>;
/**
 * this page shows information about the current participation process.
 * if no process is defined, the processmanager needs initially to define one per /process/create
 *
 * @todo move to /management/process for multi-mandant
 * @todo page-loading of FeedbackInvitations?
 */
const ProcessDetailPage: React.FC<PageProps> = (props: PageProps) => {
    const { categoryRequest, currentProcess, currentUser, fbiStateForPublicProcess, loadCurrentProcess, loadCurrentUser, loadFBIs, processRequest, roles, } = props;
    const { t } = useTranslation("common");
    const dispatch = useDispatch();
    // @todo multimandant: filter categories for current process
    const categories = categoryRequest?.getItems<ICategory>();
    useEffect(() => {
        if (!currentUser) {
            loadCurrentUser();
        }
        if (!currentProcess) {
            loadCurrentProcess();
        }
        else {
            if (MODULE_FEEDBACK_FOR_PM_AVAILABLE && currentUser?.projectMemberships?.length > 0) {
                // if user is logged in:
                // load FBIs with receiver=currentProcess and for unregistered for the logged in users
                // load FBIs for all users by default (receiver === '')
                const receivers: string[] = [""];
                if (currentUser?.projectMemberships?.length > 0
                    || isAdmin(currentUser)
                    || isProcessManager(currentUser)) {
                    // load also for registered user, if user is logged in and is member in at least 1 project
                    // or is PM or Admin
                    receivers.push(currentProcess["@id"]);
                }
                loadFBIs(currentProcess, receivers, FeedbackInvitationState.Active);
            }
        }
    }, []);
    // reload FBIs and userDiscussions, when process, user or his roles change
    useEffect(() => {
        if (MODULE_FEEDBACK_FOR_PM_AVAILABLE) {
            if (!fbiStateForPublicProcess.isLoading && currentProcess) {
                // load FBIs for all users by default (receiver === '')
                const receivers: string[] = [""];
                if (currentUser?.projectMemberships?.length > 0
                    || isAdmin(currentUser)
                    || isProcessManager(currentUser)) {
                    // load also for registered user, if user is logged in and is member in at least 1 project
                    // or is PM or Admin
                    receivers.push(currentProcess["@id"]);
                }
                loadFBIs(currentProcess, receivers, FeedbackInvitationState.Active);
            }
        }
    }, [currentProcess, currentUser, roles]);
    useEffect(() => {
        if (!categoryRequest?.loaded) {
            // @todo multimandant: filter for process
            dispatch(newLoadCollectionAction(EntityType.Category, {}, ScopeTypes.CategoriesLoading, true));
        }
    }, [currentProcess]);
    // @todo custom error message "process not found or no permission" etc.
    if (processRequest?.loadingError) {
        return <ErrorPage statusCode={404} error={processRequest?.loadingError}/>;
    }
    return <BaseLayout pageTitle={currentProcess?.name ? currentProcess.name : t("page.process.index.title")}>
    <Row>
      <Col className="col" sm={8}>
        <h1>{t("page.process.index.heading")}</h1>
        <p><TranslatedHtml content="page.process.index.intro"/></p>
        <PageError error={fbiStateForPublicProcess?.loadingError}/>

        {currentProcess
            ? <>
            <ProcessView categories={categories} currentProcess={currentProcess} roles={roles}/>
            {MODULE_FEEDBACK_FOR_PM_AVAILABLE && fbiStateForPublicProcess.loaded && fbiStateForPublicProcess.itemIds.length > 0 &&
                    <Card>
                <CardHeader>
                  <h3>{t("page.feedback.feedbackInvitations")}</h3>
                </CardHeader>
                <CardBody>
                  {fbiStateForPublicProcess.getItems<IFeedbackInvitation>().sort((d1, d2) => d1.createdAt < d2.createdAt ? 1 : -1).map(fbi => {
                            return <FeedbackInvitationQuestionCard invitation={fbi} key={"fbiqc_" + fbi.id.toString()} currentUser={currentUser}/>;
                        })}
                </CardBody>
              </Card>}
          </>
            : <>
            {processRequest?.isLoading === true && <Spinner />}
            {processRequest?.loaded &&
                    <AuthElement requiredRole={UserRole.ProcessManager} roles={roles}>
                <p>
                  {t("page.process.index.noProcess")}
                </p>
                <Link href={Routes.ProcessCreate}>
                  <a className="btn primary-btn">
                    {t("goto.createProcess")}
                  </a>
                </Link>
              </AuthElement>}
          </>}
      </Col>
    </Row>
  </BaseLayout>;
};
const __Next_Translate__Page__18c4f5174f1__ = connector(ProcessDetailPage);

    export default __appWithI18n(__Next_Translate__Page__18c4f5174f1__, {
      ...__i18nConfig,
      isLoader: true,
      skipInitialProps: false,
      loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
    });
  